// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useKeycloak } from "../context/KeycloakProvider";
import apiClient from "../config/api";
import {
  ROLE_CATEGORIES,
  filterMeaningfulRoles,
  hasSuperAdminAccess,
} from "../config/roleConfig";
import env from "../utils/env";
import { permissionGranted } from "../utils/permissionAliases";

const getAllUserRoles = (userInfo) => {
  const resourceName = env.REACT_APP_KEYCLOAK_CLIENT_ID;
  const keycloakRoles = userInfo?.resourceRoles?.[resourceName]?.roles || [];
  const realmRoles = userInfo?.roles || [];
  return [...keycloakRoles, ...realmRoles];
};

const hasMappedRole = (roles = []) => {
  const meaningful = filterMeaningfulRoles(roles);
  const requiredBusinessRoles = [
    ...ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
    ...ROLE_CATEGORIES.ADMIN_ROLES,
    ...ROLE_CATEGORIES.MANAGER_ROLES,
    ...ROLE_CATEGORIES.OPERATIONAL_ROLES,
    ...ROLE_CATEGORIES.USER_ROLES,
  ];

  return meaningful.some((role) => requiredBusinessRoles.includes(role));
};

export const usePermissions = () => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchPromise = useRef(null);
  const { authenticated, syncedUser, getUserInfo } = useKeycloak();

  const userId = useMemo(() => {
    if (syncedUser?.user_id) {
      return syncedUser.user_id;
    }
    const userInfo = getUserInfo();
    return userInfo?.id;
  }, [syncedUser?.user_id, getUserInfo]);

  const { userHasRoles, isSuperAdmin } = useMemo(() => {
    const userInfo = getUserInfo();
    const allUserRoles = getAllUserRoles(userInfo);

    const hasRoles = hasMappedRole(allUserRoles);
    const superAdmin = hasSuperAdminAccess(allUserRoles);

    return { userHasRoles: hasRoles || superAdmin, isSuperAdmin: superAdmin };
  }, [getUserInfo]);

  const cacheKey = useMemo(
    () => (userId ? `user_permissions_${userId}` : null),
    [userId],
  );

  const cachedPermissions = useMemo(() => {
    if (!cacheKey) return null;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;
      const { permissions, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return Array.isArray(permissions) ? permissions : null;
      }
    } catch {
      return null;
    }
    return null;
  }, [cacheKey]);

  const effectivePermissions = useMemo(
    () =>
      userPermissions.length > 0 ? userPermissions : cachedPermissions || [],
    [userPermissions, cachedPermissions],
  );
  const effectiveInitialized = isInitialized || cachedPermissions !== null;

  const loadFromCache = useCallback(() => {
    if (!cacheKey) return null;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { permissions, timestamp } = JSON.parse(cached);
        const isValid = Date.now() - timestamp < 5 * 60 * 1000;

        if (isValid) {
          return permissions;
        } else {
          sessionStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
      sessionStorage.removeItem(cacheKey);
    }

    return null;
  }, [cacheKey]);

  const saveToCache = useCallback(
    (permissions) => {
      if (!cacheKey) return;

      try {
        const cacheData = {
          permissions,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.error("Error saving to cache:", error);
      }
    },
    [cacheKey],
  );

  const fetchUserPermissions = useCallback(async () => {
    if (!userHasRoles) {
      setUserPermissions([]);
      setIsInitialized(true);
      setLoading(false);
      setError(null);
      return Promise.resolve([]);
    }

    if (!userId || !authenticated) return;

    if (fetchPromise.current) {
      return fetchPromise.current;
    }

    const cachedPermissions = loadFromCache();
    if (cachedPermissions) {
      setUserPermissions(cachedPermissions);
      setIsInitialized(true);
      setLoading(false);
      return Promise.resolve(cachedPermissions);
    }

    fetchPromise.current = (async () => {
      try {
        setLoading(true);
        setError(null);

        const permissionsResponse = await apiClient.get(
          `/admin/users/${userId}/permissions`,
        );

        const permissions =
          permissionsResponse.data?.permissions ||
          permissionsResponse.data?.effective_permissions ||
          [];
        const validPermissions = Array.isArray(permissions) ? permissions : [];

        setUserPermissions(validPermissions);
        setIsInitialized(true);
        saveToCache(validPermissions);
        return validPermissions;
      } catch (error) {
        console.error("Error fetching user permissions:", error);

        const code = error.response?.status;
        if (code === 401 || code === 403) {
          setError("Access denied - insufficient permissions");
        } else if (code === 404) {
          setError("No local user record found");
        } else {
          setError("Failed to fetch user permissions");
        }
        setUserPermissions([]);
        setIsInitialized(true);
        return [];
      } finally {
        setLoading(false);
        fetchPromise.current = null;
      }
    })();

    return fetchPromise.current;
  }, [userId, authenticated, loadFromCache, saveToCache, userHasRoles]);

  useEffect(() => {
    if (!authenticated) {
      setUserPermissions([]);
      setLoading(false);
      setError(null);
      setIsInitialized(false);
      fetchPromise.current = null;
      return;
    }

    if (authenticated && userId) {
      if (!userHasRoles) {
        setUserPermissions([]);
        setIsInitialized(true);
        setLoading(false);
        setError("User has no assigned roles");
        return;
      }

      fetchUserPermissions()
        .then(() => {
          setIsInitialized(true);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching user permissions:", error);
          setIsInitialized(true);
          setLoading(false);
        });
    }
  }, [authenticated, userId, userHasRoles, fetchUserPermissions]);

  useEffect(() => {
    if (!authenticated) {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("user_permissions_")) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }, [authenticated]);

  const hasAnyPermission = useCallback(
    (requiredPermissions) => {
      if (isSuperAdmin) return true;

      if (
        !Array.isArray(requiredPermissions) ||
        requiredPermissions.length === 0
      )
        return true;
      return requiredPermissions.some((permission) =>
        permissionGranted(effectivePermissions, permission),
      );
    },
    [effectivePermissions, isSuperAdmin],
  );

  const hasPermission = useCallback(
    (permission) => {
      if (isSuperAdmin) return true;

      return permissionGranted(effectivePermissions, permission);
    },
    [effectivePermissions, isSuperAdmin],
  );

  const refreshPermissions = useCallback(async () => {
    if (cacheKey) {
      sessionStorage.removeItem(cacheKey);
    }
    fetchPromise.current = null;
    return fetchUserPermissions();
  }, [cacheKey, fetchUserPermissions]);

  return {
    userPermissions: effectivePermissions,
    loading,
    error,
    hasAnyPermission,
    hasPermission,
    isInitialized: effectiveInitialized,
    refreshPermissions,
    userHasRoles,
    isSuperAdmin,
  };
};
